/**
 * ContentCard Demo Page - Dark Theme
 * TheHoleTruth V2 (theholetruth.org)
 *
 * Demonstrates the ContentCard component with dark theme
 */

import { ContentCard, ContentCardGrid } from "@hole/tailgrids-components";

export default function ContentCardDemo() {
  return (
    <div className="min-h-screen bg-surface-950">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-surface-900 to-surface-950 text-white py-20 border-b border-democracy-800">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-agency font-bold uppercase mb-4">
            ContentCard Component Demo
          </h1>
          <p className="text-xl md:text-2xl font-body text-gray-300 max-w-3xl mx-auto">
            Dark Theme Version for The Hole Truth
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center text-sm">
            <span className="px-4 py-2 bg-democracy-600/20 rounded-full border border-democracy-600/30">
              🌙 Dark Theme
            </span>
            <span className="px-4 py-2 bg-transparency-600/20 rounded-full border border-transparency-600/30">
              🎨 HOLE Foundation Branding
            </span>
            <span className="px-4 py-2 bg-ai-600/20 rounded-full border border-ai-600/30">
              📱 Fully Responsive
            </span>
          </div>
        </div>
      </section>

      {/* Demo Section 1: Latest Articles Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-agency font-bold uppercase text-center mb-4 text-white">
            Latest Articles
          </h2>
          <p className="text-center text-gray-400 font-body mb-12 max-w-2xl mx-auto">
            Example of a 3-column article grid using dark theme
          </p>

          <ContentCardGrid theme="dark" columns={{ sm: 1, lg: 3 }}>
            <ContentCard
              image="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&h=600&fit=crop"
              imageAlt="Government building and transparency"
              title="Breaking: Major FOIA Victory"
              description="Federal court rules in favor of transparency advocates, setting new precedent for public records access. This landmark decision could reshape how government agencies respond to information requests."
              buttonText="Read Full Story"
              href="/articles/foia-victory"
              theme="dark"
            />

            <ContentCard
              image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop"
              imageAlt="AI and data analysis"
              title="AI & Government Transparency"
              description="How artificial intelligence is transforming government transparency, FOIA processing, and public access to information. An in-depth analysis of the opportunities and challenges ahead."
              buttonText="Read Article"
              href="/articles/ai-transparency"
              theme="dark"
            />

            <ContentCard
              image="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop"
              imageAlt="State transparency laws and documents"
              title="State-by-State Guide"
              description="A comprehensive breakdown of transparency and open records laws across all 50 states and federal jurisdictions. Everything you need to know to file effective requests."
              buttonText="View Guide"
              href="/transparency-map"
              theme="dark"
            />
          </ContentCardGrid>
        </div>
      </section>

      {/* Demo Section 2: Investigations (2-column) */}
      <section className="py-20 bg-surface-900/50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-agency font-bold uppercase text-center mb-4 text-white">
            Investigative Reports
          </h2>
          <p className="text-center text-gray-400 font-body mb-12 max-w-2xl mx-auto">
            Example of a 2-column layout for in-depth investigations
          </p>

          <ContentCardGrid theme="dark" columns={{ sm: 1, lg: 2 }}>
            <ContentCard
              image="https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&h=600&fit=crop"
              imageAlt="Investigative journalism"
              title="The Secret Surveillance Program"
              description="Our 6-month investigation reveals previously undisclosed government surveillance programs. Based on hundreds of FOIA documents, this report exposes the scope and scale of domestic monitoring."
              buttonText="Read Investigation"
              href="/investigations/surveillance"
              theme="dark"
            />

            <ContentCard
              image="https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=800&h=600&fit=crop"
              imageAlt="Data breach investigation"
              title="Federal Agency Data Breach"
              description="Exclusive: How a major federal agency exposed millions of citizen records. Our investigation into the breach, the cover-up, and what it means for government cybersecurity."
              buttonText="Read Report"
              href="/investigations/data-breach"
              theme="dark"
            />
          </ContentCardGrid>
        </div>
      </section>

      {/* Demo Section 3: Quick Reads (4-column) */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-agency font-bold uppercase text-center mb-4 text-white">
            Quick Reads
          </h2>
          <p className="text-center text-gray-400 font-body mb-12 max-w-2xl mx-auto">
            Example of a 4-column compact grid for news briefs
          </p>

          <ContentCardGrid theme="dark" columns={{ sm: 2, lg: 4 }}>
            <ContentCard
              image="https://images.unsplash.com/photo-1495020689067-958852a7765e?w=600&h=400&fit=crop"
              imageAlt="Breaking news"
              title="FOIA Backlog Crisis"
              description="Federal agencies struggling with record FOIA request volumes. Average response times hit all-time high."
              buttonText="Details"
              href="/news/foia-backlog"
              theme="dark"
            />

            <ContentCard
              image="https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?w=600&h=400&fit=crop"
              imageAlt="Court ruling"
              title="Court Rules on Exemptions"
              description="New ruling clarifies when agencies can claim exemptions under FOIA law. Major implications for requesters."
              buttonText="Details"
              href="/news/court-ruling"
              theme="dark"
            />

            <ContentCard
              image="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=600&h=400&fit=crop"
              imageAlt="State legislation"
              title="State Law Changes"
              description="Five states pass new transparency legislation strengthening public access to government records."
              buttonText="Details"
              href="/news/state-laws"
              theme="dark"
            />

            <ContentCard
              image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop"
              imageAlt="Technology update"
              title="AI FOIA Tools Launch"
              description="HOLE Foundation releases new AI-powered tools to help citizens file more effective FOIA requests."
              buttonText="Details"
              href="/news/ai-tools"
              theme="dark"
            />
          </ContentCardGrid>
        </div>
      </section>

      {/* Demo Section 4: Without Buttons */}
      <section className="py-20 bg-surface-900/50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-agency font-bold uppercase text-center mb-4 text-white">
            Resources (No Buttons)
          </h2>
          <p className="text-center text-gray-400 font-body mb-12 max-w-2xl mx-auto">
            Simple cards with clickable titles only
          </p>

          <ContentCardGrid theme="dark" columns={{ sm: 1, lg: 3 }}>
            <ContentCard
              image="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop"
              imageAlt="FOIA templates"
              title="FOIA Templates"
              description="Free, professionally-crafted templates for filing FOIA requests in all 50 states. Includes sample language and filing instructions."
              href="/resources/templates"
              theme="dark"
              // No buttonText = no button shown
            />

            <ContentCard
              image="https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=800&h=600&fit=crop"
              imageAlt="Legal guides"
              title="Legal Guides"
              description="Comprehensive legal guides explaining your rights under federal and state transparency laws. Written by experts for everyday citizens."
              href="/resources/guides"
              theme="dark"
            />

            <ContentCard
              image="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop"
              imageAlt="Community resources"
              title="Community"
              description="Join our community of transparency advocates, journalists, and researchers. Share experiences and learn from others."
              href="/community"
              theme="dark"
            />
          </ContentCardGrid>
        </div>
      </section>

      {/* Demo Section 5: Featured Story (Full Width) */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-agency font-bold uppercase text-center mb-12 text-white">
            Featured Investigation
          </h2>

          <ContentCard
            image="https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1200&h=600&fit=crop"
            imageAlt="Major investigation"
            title="The Transparency Project: A Year-Long Investigation"
            description="Over the past year, The Hole Truth has filed over 1,000 FOIA requests across all federal agencies. This landmark investigation reveals systemic failures in government transparency, delayed responses, improper redactions, and agencies that simply ignore the law. Our findings show that despite decades of transparency legislation, the public's right to know is under threat."
            buttonText="Read Full Investigation"
            href="/investigations/transparency-project"
            theme="dark"
            className="max-w-4xl mx-auto"
          />
        </div>
      </section>

      {/* Design Tokens Reference */}
      <section className="py-20 bg-democracy-900/30 border-t border-democracy-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-agency font-bold uppercase mb-8 text-white">
            Design Tokens Used
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-surface-900 p-6 rounded-lg border border-surface-800">
              <h3 className="text-2xl font-agency font-bold mb-4 text-democracy-400">Colors</h3>
              <ul className="space-y-2 font-body text-gray-300">
                <li>Background: surface-950</li>
                <li>Title: white</li>
                <li>Hover: democracy-500</li>
                <li>Border: surface-800</li>
              </ul>
            </div>
            <div className="bg-surface-900 p-6 rounded-lg border border-surface-800">
              <h3 className="text-2xl font-agency font-bold mb-4 text-transparency-400">Typography</h3>
              <ul className="space-y-2 font-body text-gray-300">
                <li>Headings: AgencyFB</li>
                <li>Body: Muller Next</li>
                <li>Uppercase titles</li>
                <li>Bold font weight</li>
              </ul>
            </div>
            <div className="bg-surface-900 p-6 rounded-lg border border-surface-800">
              <h3 className="text-2xl font-agency font-bold mb-4 text-ai-400">Effects</h3>
              <ul className="space-y-2 font-body text-gray-300">
                <li>Image zoom on hover</li>
                <li>Shadow elevation</li>
                <li>Smooth transitions</li>
                <li>Gradient overlays</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
